import {
  Box,
  HStack,
  Circle,
  useColorModeValue,
} from "@chakra-ui/react";
import { AsyncSelect, Select } from "chakra-react-select";
import { ReactNode } from "react";

interface Option {
  label: string;
  value: string;
  color?: string;
  icon?: ReactNode;
}

interface TableFilterSelectProps {
  placeholder?: string;
  value: Option | Option[] | null;
  onChange: (option: Option | Option[] | null) => void;
  loadOptions?: (inputValue: string) => Promise<Option[]>;
  defaultOptions: Option[];
  minWidth?: string;
  isClearable?: boolean;
  isMulti?: boolean;
}

export default function TableFilterSelect({
  placeholder = "Filter by...",
  value,
  onChange,
  loadOptions,
  defaultOptions,
  minWidth = "280px",
  isClearable = true,
  isMulti = false,
}: TableFilterSelectProps) {
  const controlBorderColor = useColorModeValue("gray.300", "whiteAlpha.300");
  const controlBorderHoverColor = useColorModeValue("gray.400", "whiteAlpha.400");
  const controlBg = useColorModeValue("white", "gray.800");
  const controlTextColor = useColorModeValue("gray.800", "gray.100");
  const placeholderColor = useColorModeValue("gray.600", "gray.400");
  const optionBg = useColorModeValue("white", "gray.800");
  const optionHoverBg = useColorModeValue("gray.50", "whiteAlpha.100");
  const menuBorderColor = useColorModeValue("gray.200", "whiteAlpha.200");

  const formatOptionLabel = (option: Option) => {
    if (option.icon) {
      return (
        <HStack spacing={2}>
          {option.icon}
          <Box>{option.label}</Box>
        </HStack>
      );
    }
    if (option.color) {
      return (
        <HStack spacing={2}>
          <Circle size="8px" bg={option.color} />
          <Box>{option.label}</Box>
        </HStack>
      );
    }
    return option.label;
  };

  const SelectComponent = loadOptions ? AsyncSelect : Select;

  return (
    <Box minWidth={minWidth}>
      <SelectComponent
        isClearable={isMulti ? false : isClearable}
        placeholder={placeholder}
        loadOptions={loadOptions}
        options={!loadOptions ? defaultOptions : undefined}
        defaultOptions={loadOptions ? defaultOptions : undefined}
        value={value}
        onChange={(newValue) => {
          onChange(
            Array.isArray(newValue)
              ? [...newValue]
              : (newValue as Option | null),
          );
        }}
        formatOptionLabel={formatOptionLabel}
        isMulti={isMulti}
        closeMenuOnSelect={!isMulti}
        hideSelectedOptions={false}
        controlShouldRenderValue={!isMulti}
        chakraStyles={{
          control: (provided, state) => ({
            ...provided,
            borderColor: state.isFocused ? "cdsIndigo.400" : controlBorderColor,
            borderWidth: "1px",
            borderRadius: "md",
            minHeight: "36px",
            height: "36px",
            fontSize: "14px",
            paddingLeft: "8px",
            paddingRight: "8px",
            boxShadow: "none",
            cursor: "pointer",
            backgroundColor: controlBg,
            color: controlTextColor,
            _focus: {
              boxShadow: "none",
              borderColor: "cdsIndigo.400",
            },
            _focusVisible: {
              boxShadow: "none",
              borderColor: "cdsIndigo.400",
            },
            _focusWithin: {
              boxShadow: "none",
              borderColor: "cdsIndigo.400",
              borderBottomColor: "cdsIndigo.400",
            },
            _hover: {
              borderColor: state.isFocused ? "cdsIndigo.400" : controlBorderHoverColor,
              backgroundColor: optionHoverBg,
            },
          }),
          placeholder: (provided) => ({
            ...provided,
            color: placeholderColor,
            fontSize: "14px",
            fontWeight: "semibold",
          }),
          valueContainer: (provided) => ({
            ...provided,
            padding: "0 4px",
          }),
          input: (provided) => ({
            ...provided,
            margin: "0",
            padding: "0",
            fontSize: "14px",
            color: controlTextColor,
          }),
          singleValue: (provided) => ({
            ...provided,
            display: isMulti ? "none" : undefined,
            fontSize: "14px",
            fontWeight: "semibold",
            color: controlTextColor,
          }),
          multiValue: (provided) => ({
            ...provided,
            display: "none",
          }),
          option: (provided, state) => ({
            ...provided,
            fontSize: "14px",
            backgroundColor: state.isFocused ? optionHoverBg : optionBg,
            color: controlTextColor,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            _after: isMulti && state.isSelected ? {
              content: '"✓"',
              marginLeft: "8px",
              fontWeight: "bold",
            } : {},
          }),
          menu: (provided) => ({
            ...provided,
            backgroundColor: optionBg,
            borderColor: menuBorderColor,
            boxShadow: "md",
          }),
        }}
      />
    </Box>
  );
}
